/**
 * useContractsKanbanRealtime - Realtime centralizado do Kanban de Contratos
 *
 * Um único canal por tenant escuta a tabela `contracts`. Quando o status muda,
 * o card é removido da coluna antiga (atualização otimista do cache) e a coluna
 * nova + as contagens são invalidadas. Isso reproduz o comportamento do Kanban
 * de filas: os cards andam sozinhos, sem arrastar e soltar.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import { useEffect } from 'react';
import { useQueryClient, InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { resolveContractStatusGroup } from '@/components/contracts/kanban/contractKanbanColumns';
import type { KanbanContract } from '@/hooks/useContractKanbanColumn';
import type { ContractStatusGroup } from '@/components/contracts/kanban/contractKanbanColumns';

// Logger centralizado
const logger = createLogger('ContractsKanbanRealtime');

/**
 * Ativa o Realtime do Kanban de Contratos enquanto o board estiver montado.
 *
 * @param enabled - Só assina quando o modo Kanban está ativo
 */
export function useContractsKanbanRealtime(enabled: boolean = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !currentTenant?.id) return;

    const tenantId = currentTenant.id;

    /** Remove o contrato de todas as colunas em cache, exceto da coluna informada */
    const removeFromOtherColumns = (contractId: string, keepGroup: ContractStatusGroup | null) => {
      const queries = queryClient.getQueriesData<InfiniteData<KanbanContract[]>>({
        queryKey: ['contract-kanban-column', tenantId],
      });

      queries.forEach(([key, data]) => {
        if (!data) return;
        const group = key[2] as ContractStatusGroup | undefined;
        if (group && group === keepGroup) return;

        const exists = data.pages.some((page) => page.some((c) => c.id === contractId));
        if (!exists) return;

        queryClient.setQueryData<InfiniteData<KanbanContract[]>>(key, {
          ...data,
          pages: data.pages.map((page) => page.filter((c) => c.id !== contractId)),
        });
      });
    };

    /** Invalida uma coluna específica (para o card aparecer com os joins completos) */
    const invalidateGroup = (group: ContractStatusGroup | null) => {
      if (!group) return;
      queryClient.invalidateQueries({
        queryKey: ['contract-kanban-column', tenantId, group],
      });
    };

    const channel = supabase
      .channel(`contracts-kanban-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const newRow = payload.new as { id?: string; status?: string } | null;
          const oldRow = payload.old as { id?: string; status?: string } | null;
          const contractId = newRow?.id || oldRow?.id;
          if (!contractId) return;

          const newGroup = resolveContractStatusGroup(newRow?.status);
          const oldGroup = resolveContractStatusGroup(oldRow?.status);

          logger.debug('Evento realtime de contrato', {
            event: payload.eventType,
            contractId,
            oldGroup,
            newGroup,
          });

          if (payload.eventType === 'DELETE') {
            removeFromOtherColumns(contractId, null);
          } else {
            // Remove das colunas antigas e recarrega a coluna de destino
            removeFromOtherColumns(contractId, newGroup);
            invalidateGroup(newGroup);
          }

          // Contagens/valores sempre são recalculados
          queryClient.invalidateQueries({ queryKey: ['contract-kanban-counts', tenantId] });
          // Mantém os KPIs do topo em sincronia com o board
          queryClient.invalidateQueries({ queryKey: ['contract-list-kpis'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, currentTenant?.id, queryClient]);
}
